import { Info } from 'lucide-react'

import greenCheckIcon from '../assets/green-check.png'
import greyLockIcon from '../assets/grey-lock-no-background.png'
import purpleFlagIcon from '../assets/purple-flag.png'
import { sortedTiers, tierState, type TierState } from '../lib/pass-ui'
import type { Pass, PassRewardTier } from '../types/pass'

import { cn } from '@/shared/lib/utils'

import { RewardStack } from './RewardVisual'

interface PassRewardTrailProps {
  pass: Pass
}

const NODE_WIDTH_MOBILE = 138
const NODE_WIDTH_DESKTOP = 220
const NODE_GAP_MOBILE = 16
const NODE_GAP_DESKTOP = 32

const PILL_CLASS: Record<TierState, string> = {
  delivered: 'bg-[#22A91E] text-white',
  available: 'bg-[#7F4CFF] text-white',
  locked: 'bg-[#7A739A] text-white'
}

const STATUS_TEXT_CLASS: Record<TierState, string> = {
  delivered: 'text-[#17901C]',
  available: 'text-[#5D2CE0]',
  locked: 'text-[#3C3467]'
}

const STATUS_LABEL: Record<TierState, string> = {
  delivered: 'Concluído',
  available: 'Disponível',
  locked: 'Bloqueado'
}

// A recompensa bloqueada aparece dessaturada e com o cadeado por cima: o aluno
// vê o que vai ganhar, sem confundir com o que já é dele.
const REWARD_STATE_CLASS: Record<TierState, string> = {
  delivered: 'drop-shadow-[0_0_20px_rgba(111,255,86,0.38)]',
  available: 'drop-shadow-[0_0_18px_rgba(154,83,255,0.38)]',
  locked: 'opacity-60 grayscale'
}

function RewardTrailNode({
  tier,
  state
}: {
  tier: PassRewardTier
  state: TierState
}) {
  const statusIcon = state === 'delivered' ? greenCheckIcon : state === 'locked' ? greyLockIcon : null

  return (
    <div className="relative z-10 flex w-[138px] shrink-0 flex-col items-center text-center sm:w-[220px]">
      <span
        className={cn(
          'rounded-[1rem] px-3.5 py-1.5 text-[0.95rem] font-black tracking-[-0.04em] shadow-[0_6px_16px_rgba(0,0,0,0.18)] sm:px-4 sm:py-2 sm:text-[1.7rem]',
          PILL_CLASS[state]
        )}
      >
        {tier.percent_threshold}%
      </span>

      <div className="relative mt-3 sm:mt-4">
        <RewardStack
          items={tier.pass_reward_tier_items}
          frameClassName="size-20 sm:size-28"
          className={REWARD_STATE_CLASS[state]}
        />

        {state === 'locked' ? (
          <img
            src={greyLockIcon}
            alt=""
            className="pointer-events-none absolute inset-0 m-auto size-8 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)] sm:size-11"
          />
        ) : null}
      </div>

      <div className="mt-3.5 flex min-h-11 items-center justify-center rounded-[1rem] bg-white px-3 py-2 shadow-[0_12px_24px_rgba(10,3,31,0.16)] sm:mt-4 sm:min-h-14 sm:px-5 sm:py-2.5">
        {statusIcon ? (
          <img src={statusIcon} alt="" className="mr-1.5 size-[18px] shrink-0 sm:mr-2 sm:size-6" />
        ) : null}
        <span
          className={cn(
            'text-[0.9rem] font-black tracking-[-0.04em] sm:text-[1.45rem]',
            STATUS_TEXT_CLASS[state]
          )}
        >
          {STATUS_LABEL[state]}
        </span>
      </div>
    </div>
  )
}

export function PassRewardTrail({ pass }: PassRewardTrailProps) {
  const tiers = sortedTiers(pass)
  const hasConnectorRoad = tiers.length > 1
  const mobileTrackWidth =
    tiers.length * NODE_WIDTH_MOBILE + (tiers.length - 1) * NODE_GAP_MOBILE
  const desktopTrackWidth =
    tiers.length * NODE_WIDTH_DESKTOP + (tiers.length - 1) * NODE_GAP_DESKTOP
  const trackStyle = {
    '--trail-mobile-width': `${mobileTrackWidth}px`,
    '--trail-desktop-width': `${desktopTrackWidth}px`,
    '--trail-mobile-line-left': `${NODE_WIDTH_MOBILE / 2}px`,
    '--trail-desktop-line-left': `${NODE_WIDTH_DESKTOP / 2}px`,
    '--trail-mobile-line-width': `${mobileTrackWidth - NODE_WIDTH_MOBILE}px`,
    '--trail-desktop-line-width': `${desktopTrackWidth - NODE_WIDTH_DESKTOP}px`
  } as React.CSSProperties

  if (tiers.length === 0) return null

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,#1E0A49_0%,#160637_100%)] px-5 py-6 shadow-[0_20px_45px_rgba(8,3,24,0.4)] sm:px-7 sm:py-7">
      <div className="flex items-center gap-3">
        <img src={purpleFlagIcon} alt="" className="size-8 shrink-0 sm:size-10" />
        <h3 className="text-[1.45rem] font-black tracking-[-0.05em] text-white sm:text-[2.4rem]">
          Trilha de Recompensas
        </h3>
      </div>

      <div className="mt-6 overflow-x-auto pb-2">
        <div
          className={cn(
            'relative pb-3 pt-1',
            hasConnectorRoad
              ? 'w-[var(--trail-mobile-width)] px-0 sm:w-[var(--trail-desktop-width)]'
              : 'mx-auto w-full max-w-[170px] px-0 sm:max-w-[220px]'
          )}
          style={hasConnectorRoad ? trackStyle : undefined}
        >
          {hasConnectorRoad ? (
            <>
              <div className="absolute left-[var(--trail-mobile-line-left)] top-[5.2rem] h-3 w-[var(--trail-mobile-line-width)] rounded-full bg-[#6B33DA] opacity-60 sm:left-[var(--trail-desktop-line-left)] sm:top-[8.1rem] sm:w-[var(--trail-desktop-line-width)]" />
              <div className="absolute left-[var(--trail-mobile-line-left)] top-[5.2rem] h-3 w-[var(--trail-mobile-line-width)] rounded-full border-2 border-dashed border-pass-trail-dash opacity-95 sm:left-[var(--trail-desktop-line-left)] sm:top-[8.1rem] sm:w-[var(--trail-desktop-line-width)]" />
            </>
          ) : null}

          <div className={cn('flex', hasConnectorRoad ? 'gap-4 sm:gap-8' : 'justify-center')}>
            {tiers.map(tier => (
              <RewardTrailNode
                key={tier.id}
                tier={tier}
                state={tierState(tier)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/8 px-3 py-2.5 text-[0.8rem] font-semibold tracking-[-0.02em] text-[#BFA7ED] sm:px-4 sm:py-3 sm:text-base">
        <Info className="size-4 shrink-0" />
        Novos objetivos e recompensas são liberados ao longo do passe.
      </div>
    </section>
  )
}
